import { getConfigForImportedTest } from 'Components/Worksheets/Config';
import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import {
  IQuantity,
  isGreaterThanOrEqualTo as isIQuantityGreaterThanOrEqualTo,
  isLessThan as isIQuantityLessThan,
  toIQuantity,
} from 'Data/ViewModels/Core/IQuantity';
import * as oPath from '../../../../../Lib/Object/Path/Immutable';
import { mergeConfig } from '../../../Core/Helpers/configHelpers';
import qestIds from '../../../QestIds';
import getDerivedFromMeasurementsConfig from '../../derivedFromMeasurementsConfig';
import getMeasuredUsingConfig from '../../measuredUsingConfig';
import getLabelConfig from './labelConfig';

const getDefaultReadability = (preparedSampleMass: IQuantity) => {
  //Set Default Readability at 0.1
  let defaultReadability = 0.1;

  if (isIQuantityLessThan(preparedSampleMass, toIQuantity(100, 'g'))) {
    defaultReadability = 0.01;
  } else if (
    isIQuantityGreaterThanOrEqualTo(preparedSampleMass, toIQuantity(100, 'g')) &&
    isIQuantityLessThan(preparedSampleMass, toIQuantity(500, 'g'))
  ) {
    defaultReadability = 0.1;
  } else if (isIQuantityGreaterThanOrEqualTo(preparedSampleMass, toIQuantity(500, 'g'))) {
    defaultReadability = 1;
  }

  return {
    defaultReadability,
    defaultUom: 'g',
  };
};

const getConfig = (prefix: string) =>
  mergeConfig(
    {
      [`${prefix}.Preparation.CuringTime`]: { uom: 'h', format: { nearest: 0.1 } },
      [`${prefix}.Preparation.NumberOfBlows`]: { format: { nearest: 1 } },
      [`${prefix}.Preparation.ConePenetration`]: { uom: 'mm', format: { nearest: 0.1 } },
      [`${prefix}.Preparation.InternalLengthOfMould`]: { uom: 'mm', format: { nearest: 1 } },
      [`${prefix}.Preparation.InitialDryingTime`]: { uom: 'h', format: { nearest: 0.1 } },
      [`${prefix}.Preparation.OvenTemperature`]: { uom: '°C', format: { nearest: 1 } },

      //Sample Preparation
      ...getConfigForImportedTest(
        qestIds.psdSieveAnalysis.AS_1289_3_6_1.Rev2009,
        `${prefix}.SamplePreparation.AssociatedPercentagePassing`,
        [
          qestIds.psdSieveAnalysis.AS_1289_3_6_3,
          qestIds.psdSieveAnalysis.AS_1289_3_6_1.RevOriginal,
          qestIds.psdSieveAnalysis.AS_1289_3_6_1.RevOriginal2,
          qestIds.psdSieveAnalysis.AS_1289_3_6_1.Reduced,
        ]
      ),
      [`${prefix}.SamplePreparation.PercentPassing`]: { format: { nearest: 0.1 } },
      [`${prefix}.SamplePreparation.DryingTemperature`]: { uom: '°C', format: { nearest: 1 } },
      [`${prefix}.SamplePreparation.AssociatedPercentagePassing.ExternalTest.Results.PercentagePassing_425_Sieve`]: {
        format: { nearest: 1 },
      },

      //Measurements
      [`${prefix}.Measurements.TopSurfaceLength`]: { uom: 'mm', format: { nearest: 1 } },
      [`${prefix}.Measurements.BottomSurfaceLength`]: { uom: 'mm', format: { nearest: 1 } },
      [`${prefix}.Measurements.SoilBarLength`]: { uom: 'mm', format: { nearest: 1 } },
      [`${prefix}.Measurements.LongitudinalShrinkage`]: { uom: 'mm', format: { nearest: 1 } },
      [`${prefix}.Measurements.LinearShrinkage`]: { uom: '%', format: { nearest: 0.5 } },
    },
    getLabelConfig(prefix),
    getMeasuredUsingConfig(prefix),
    getDerivedFromMeasurementsConfig(prefix),
    {
      [`${prefix}.Preparation.Balance`]: (configArgs: IConfigFunctionArgs) =>
        getDefaultReadability(oPath.get(configArgs.worksheet, `${prefix}.Preparation.PreparedSampleMass`) as IQuantity),
    }
  );

export { getConfig };

export default {
  [qestIds.linearShrinkage.AS_1289_5_4_1.rev2008]: getConfig,
};
